/**
 * `DataProvider`: the Query client and its persisted cache, the session, and
 * the outbox, wired to the device. `app.tsx` mounts it around the router.
 *
 * `PersistQueryClientProvider` restores asynchronously: queries render with
 * `isRestoring` until the cache is back. Nothing here waits for it — the app
 * shell paints at once, and a screen that needs data (T60 and later) handles
 * `useIsRestoring()` itself.
 */
import type { KeyValueStorage } from "@repo/client/i18n";
import { createOutboxStore } from "@repo/client/offline";
import { INTAKE_STORAGE_KEY } from "@repo/client/onboarding";
import type { SessionEndOutput } from "@repo/contract/training";
import { focusManager, onlineManager } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { type ReactNode, useEffect } from "react";
import { clearAllComposers } from "../features/coach/composer-store";
import { intakeStore } from "../features/onboarding/storage";
import { forgetClockSync } from "../features/profile/use-clock-sync";
import { useRecentsStore } from "../features/search/recents-store";
import { forgetCheckinState } from "../features/today/checkin-state";
import { workoutStore } from "../features/workout/store/instance";
import {
  clearWidgetSnapshot,
  getNetworkState,
  loadClipCache,
  localClipSrc,
  onAppActiveChange,
  onNetworkChange,
  preferences,
} from "../native";
import { forgetWeightUnit } from "../stores/weight-unit";
import { clipUrl, registerClipsOnDisk } from "../ui/media";
import { createAccountScope } from "./account";
import { type AccountCleanupSteps, runAccountCleanup } from "./account-cleanup";
import { ApiProvider, api, onApiSessionEnded } from "./api";
import { rememberEndAnswer } from "./end-answer";
import { wireFocus } from "./focus";
import { registerMutationDefaults } from "./mutations";
import { wireOnline } from "./online";
import { wireOptimisticOverlay } from "./outbox-optimistic";
import { createOutboxRunner, OutboxContext } from "./outbox-runner";
import {
  createOutboxSend,
  exclusionAnswers,
  measurementIds,
  outboxInvalidations,
} from "./outbox-send";
import { persistOptions, queryPersister } from "./persist";
import { invalidateKeys, queryClient } from "./query-client";
import { SessionProvider } from "./session";
import { requestPersistentStorage } from "./storage-persistence";

registerMutationDefaults(queryClient, api);

/**
 * Mutations restored paused from the persisted cache do not resume on their
 * own when the device is already online (TanStack waits for the next online
 * event). Resume them once the cache is back.
 */
function resumeAfterRestore(): void {
  queryClient.resumePausedMutations().catch((error: unknown) => {
    console.warn("[data] resuming paused mutations failed", error);
  });
}

/** Capacitor Preferences (plan D21): Android may evict a WebView's storage. */
const preferencesStorage: KeyValueStorage = {
  getItem: (key) => preferences.get(key),
  removeItem: (key) => preferences.remove(key),
  setItem: (key, value) => preferences.set(key, value),
};

/**
 * The app's ONE answer to "whose data is on this device" (`account.ts`).
 *
 * It lives here rather than in `account.ts` because this is the file that
 * binds Capacitor: the scope's own module stays native-free, so its tests and
 * the session controller's run in Node. `code-screen.tsx` imports it from
 * here to claim the account a sign-in just verified, and the per-account
 * cleanup below is registered on its `onLeave`.
 */
export const accountScope = createAccountScope(preferencesStorage);

/**
 * THE list of what on this device belongs to an account rather than to the
 * phone (T35). One place, so that adding a per-person store is one line here
 * rather than a search through every feature.
 *
 * The stores are imported STATICALLY on purpose. A registry each feature fills
 * at import time would miss exactly the ones that matter — search and the
 * trainer are lazy chunks, and a person who never opened them this run would
 * leave their stored recents and drafts behind. Measured on a `--mode web`
 * build: the first-load chunk grows 12 kB minified (4.3 kB gzipped), almost
 * all of it `@repo/client/onboarding`, and the lazy chunks that held those
 * modules shrink by about as much — the app's total JavaScript moves by 790
 * bytes. The workout store was already in the first chunk.
 *
 * What is NOT here: the Query cache and the push state, which every session
 * end already drops (`session.tsx`, `features/push/runtime.ts`); the chat keys,
 * which carry the account id already; the theme, the locale and the
 * diagnostics, which belong to the device; and the OUTBOX — another account's
 * queued writes are kept, hidden and sent when it signs in again (plan D12,
 * T28/T34), never discarded here.
 */
const ACCOUNT_DATA: AccountCleanupSteps = {
  checkins: forgetCheckinState,
  /**
   * What this device last told the server about its clock (T144). The two keys
   * are per ORIGIN, not per account: left behind, the next person's zone is
   * never sent and their streak is counted in UTC. It removes only — the next
   * launch, which holds their credential, does the reporting.
   */
  clock: forgetClockSync,
  composers: clearAllComposers,
  intake: () => intakeStore.removeItem(INTAKE_STORAGE_KEY),
  recents: () => useRecentsStore.getState().clear(),
  /** The home-screen widgets (`D280`): the next person must not see this
   *  one's workout on the launcher. */
  widgets: clearWidgetSnapshot,
  /**
   * The unit the leaving account reads loads in (`D266`). The next person's
   * arrives with their own `me`; until then, kilograms.
   */
  weightUnit: forgetWeightUnit,
  /**
   * The active workout, cleared only once the store has read what is stored:
   * clearing before hydration finishes would be overwritten by the session
   * arriving a moment later (`store/hydration.ts`, plan D22).
   */
  workout: async () => {
    await workoutStore.hydration.whenHydrated();
    workoutStore.setState({ session: null });
  },
};

/**
 * Registered once, at module scope, not in an effect: `DataProvider` may
 * remount, and a second registration would run every step twice.
 *
 * It runs on a deliberate sign-out and when a DIFFERENT account signs in —
 * never when a refresh fails and the same person signs back in, whose workout
 * has to survive that (`account.ts`).
 */
accountScope.onLeave(() => runAccountCleanup(ACCOUNT_DATA));

const outboxStore = createOutboxStore(preferencesStorage, undefined, {
  onCorrupt: (report) => {
    console.error("[data] outbox value was corrupt and set aside", report);
  },
});

const outboxRunner = createOutboxRunner({
  invalidate: (keys) => invalidateKeys(queryClient, keys),
  invalidatesFor: outboxInvalidations(api),
  network: onlineManager,
  onAppActive: onAppActiveChange,
  /**
   * The end's answer is the only place the next session's changes exist
   * (`end-answer.ts`). It is kept before the runner invalidates, and the key
   * it is kept under is outside everything the runner invalidates. The cast
   * is safe: `endSession` already parsed this value with `sessionEndOutput`
   * (`@repo/client/data/workout`), and `session.end` is the only kind whose
   * answer has that shape.
   */
  onSent: (item, answer) => {
    if (item.kind === "session.end") {
      rememberEndAnswer(queryClient, item.entityId, answer as SessionEndOutput);
    }
    // The row's id, for an undo queued behind the add (`D194`): only this
    // answer carries it. `recordMeasurement` parsed it already.
    if (item.kind === "measurement.add") {
      const { measurementId } = answer as { measurementId: string };
      measurementIds.set(item.entityId, measurementId);
    }
    if (item.kind === "exclusion.add") {
      exclusionAnswers.set(
        item.entityId,
        (answer as { created: boolean }).created
      );
    }
  },
  onScopeChange: accountScope.subscribe,
  /**
   * Whose writes this device may send. Until `load()` below resolves this is
   * `{ accountId: null }`, which sends NOTHING (T28) — so no queue left by a
   * session that ended without the person choosing it can drain under a boot
   * that does not yet know whose device this is.
   */
  scope: () => ({ accountId: accountScope.current() }),
  send: createOutboxSend(api),
  store: outboxStore,
});

async function clearCaches(): Promise<void> {
  queryClient.clear();
  await queryPersister.removeClient();
}

const drainAfterSignIn = () => outboxRunner.trigger();

export function DataProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    const unwireOnline = wireOnline(onlineManager, {
      getNetworkState,
      onNetworkChange,
    });
    // A resume refreshes the token before the screens refetch (`D194`).
    const unwireFocus = wireFocus(focusManager, {
      onAppActiveChange,
      warmTokens: () => api.warmTokens(),
    });
    requestPersistentStorage().catch(() => undefined);
    // The clips this device downloaded play from disk (`D194`).
    registerClipsOnDisk(localClipSrc);
    loadClipCache(clipUrl).catch(() => undefined);
    const stopOutbox = outboxRunner.start();
    // The queued account writes stay drawn across refetches (`D194`).
    const stopOverlay = wireOptimisticOverlay(queryClient, outboxStore);
    // `start()` already drained, under the empty scope, which sent nothing.
    // This is the pass that may: the device's account is known by now.
    accountScope.load().then(
      () => outboxRunner.trigger(),
      (error: unknown) => {
        console.warn("[data] account scope load failed", error);
      }
    );
    return () => {
      stopOverlay();
      stopOutbox();
      unwireFocus();
      unwireOnline();
    };
  }, []);

  return (
    <ApiProvider value={api}>
      <PersistQueryClientProvider
        client={queryClient}
        onSuccess={resumeAfterRestore}
        persistOptions={persistOptions}
      >
        <OutboxContext.Provider value={outboxRunner}>
          <SessionProvider
            accountScope={accountScope}
            api={api}
            clearCaches={clearCaches}
            onSignedIn={drainAfterSignIn}
            outbox={outboxStore}
            subscribeSessionEnded={onApiSessionEnded}
          >
            {children}
          </SessionProvider>
        </OutboxContext.Provider>
      </PersistQueryClientProvider>
    </ApiProvider>
  );
}
